import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, FileText, Wallet, Download, Trash2, Eye, Edit, FileDown } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import InvoiceModal from '../components/modals/InvoiceModal';
import PaymentModal from '../components/modals/PaymentModal';
import CreditNoteModal from '../components/modals/CreditNoteModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import EntityModal from '../components/modals/EntityModal';
import { useAppStore } from '../store';
import { formatCurrency, formatDate } from '../utils/formatters';
import { generateInvoicePDF, generatePaymentPDF } from '../utils/pdfGenerator';
import { formatArgentinaDate, adjustDateForInput } from '../utils/dateUtils';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ClientAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<'all' | 'A' | 'B' | 'C' | 'X'>('all');
  const [selectedTransactionType, setSelectedTransactionType] = useState<'all' | 'invoice' | 'payment'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'invoice' | 'payment' } | null>(null);
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set());
  
  const { clients, invoices, payments = [], deleteInvoice, deletePayment, calculateEntityBalance, updateClient } = useAppStore();
  
  // Find the client - moved outside of conditional rendering
  const client = clients.find(c => c.id === id);
  
  // Calculate current balance dynamically - moved outside of conditional rendering
  const currentBalance = useMemo(() => {
    if (!id) return 0;
    return calculateEntityBalance(id, 'client');
  }, [id, calculateEntityBalance, invoices, payments]);
  
  // Prepare client transactions - moved outside of conditional rendering
  const clientTransactions = useMemo(() => {
    if (!id) return [];
    
    // Get all payments for this client, including those not associated with invoices
    const clientPayments = payments.filter(payment => {
      if (payment.invoice_id) {
        // Payment associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice?.entity_id === id && invoice?.entity_type === 'client' && !payment.voided;
      } else if (payment.reference === `client:${id}` && !payment.voided) {
        // Account payment for this client
        return true;
      }
      return false;
    });
    
    return [
      ...invoices
        .filter(inv => inv.entity_id === id && inv.entity_type === 'client' && !inv.voided)
        .map(inv => ({
          id: inv.id,
          date: inv.date,
          due_date: inv.due_date,
          timestamp: new Date(inv.date).getTime(),
          createdAt: new Date(inv.created_at).getTime(),
          description: `Factura ${inv.number}`,
          type: 'invoice' as const,
          invoiceType: inv.type,
          amount: inv.total,
          voided: Boolean(inv.voided),
          document: () => generateInvoicePDF(inv, client?.name || 'Cliente'),
          invoice_file: inv.invoice_file,
        })),
      ...clientPayments
        .map(pay => {
          const invoice = pay.invoice_id ? invoices.find(inv => inv.id === pay.invoice_id) : null;
          
          // Skip payments that don't belong to this client
          if (!invoice && pay.reference !== `client:${id}`) {
            return null;
          }
          
          const description = pay.invoice_id 
            ? (pay.invoice_type === 'credit_note' 
                ? `Nota de crédito ${pay.reference || ''}` 
                : `Pago ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`)
            : `Pago a cuenta ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`;
          
          return {
            id: pay.id,
            date: pay.date,
            timestamp: new Date(pay.date).getTime(),
            createdAt: new Date(pay.created_at).getTime(),
            description,
            type: 'payment' as const,
            invoiceType: pay.invoice_type,
            amount: pay.amount,
            voided: Boolean(pay.voided),
            document: () => generatePaymentPDF(pay, client?.name || 'Cliente'),
          };
        }).filter(Boolean), // Remove null entries
    ] as any[];
  }, [id, invoices, payments, client]);
  
  // Filter transactions - moved outside of conditional rendering
  const filteredTransactions = useMemo(() => {
    return clientTransactions.filter(t => {
      const transactionDate = new Date(t.date);
      
      // Filter by date range
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59); // Include the entire end day
        return transactionDate >= start && transactionDate <= end;
      }
      
      // Filter by period
      if (period !== 'all') {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
        
        switch (period) {
          case 'month':
            return transactionDate >= firstDayOfMonth;
          case 'quarter':
            return transactionDate >= firstDayOfQuarter;
          case 'year':
            return transactionDate >= firstDayOfYear;
          default:
            return true;
        }
      }

      return true;
    }).filter(t => {
      // Filter by transaction type
      if (selectedTransactionType !== 'all') {
        return t.type === selectedTransactionType;
      }
      return true;
    }).filter(t => {
      // Filter by invoice type
      if (selectedInvoiceType !== 'all' && t.type === 'invoice') {
        return t.invoiceType === selectedInvoiceType;
      }
      return true;
    }).filter(t => !deletedItems.has(t.id));
  }, [clientTransactions, period, startDate, endDate, selectedTransactionType, selectedInvoiceType, deletedItems]);
  
  // Sort transactions by date (most recent first) and then by creation time if dates are the same
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      // First compare by date
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      // If dates are the same, compare by creation time (most recent first)
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions]);
  
  // Calculate totals - moved outside of conditional rendering
  const totalBilling = useMemo(() => {
    return filteredTransactions
      .filter(t => t.type === 'invoice')
      .reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);
  
  const totalPayments = useMemo(() => {
    return filteredTransactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);

  // Calculate running balances for each transaction
  const transactionsWithBalances = useMemo(() => {
    // First, sort transactions by date (oldest first) to calculate running balance correctly
    const chronologicalTransactions = [...filteredTransactions].sort((a, b) => {
      // First compare by date
      const dateComparison = a.timestamp - b.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      // If dates are the same, compare by creation time (oldest first)
      return a.createdAt - b.createdAt;
    });
    
    // Calculate running balance for each transaction
    let runningBalance = 0;
    const withBalances = chronologicalTransactions.map(transaction => {
      if (transaction.type === 'invoice') {
        runningBalance += transaction.amount;
      } else if (transaction.invoiceType === 'credit_note') {
        runningBalance -= transaction.amount; // Credit notes decrease the balance (reduce what client owes)
      } else {
        runningBalance -= transaction.amount;
      }
      
      return {
        ...transaction,
        runningBalance
      };
    });
    
    // Now sort back to most recent first for display
    return withBalances.sort((a, b) => {
      // First compare by date
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      // If dates are the same, compare by creation time (most recent first)
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions]);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'invoice') {
        await deleteInvoice(itemToDelete.id);
      } else {
        await deletePayment(itemToDelete.id);
      }
      
      // Mark item as deleted in local state
      setDeletedItems(prev => new Set([...prev, itemToDelete.id]));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  // Generate and download PDF report
  const generatePDF = () => {
    if (!client) return;
    
    // Create a new PDF document
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Cuenta de Cliente: ${client.name}`, 14, 20);
    
    // Add period information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    let periodText = 'Período: ';
    if (period === 'all') {
      periodText += 'Todo el historial';
    } else if (period === 'custom' && startDate && endDate) {
      periodText += `${formatArgentinaDate(startDate)} - ${formatArgentinaDate(endDate)}`;
    } else {
      periodText += period === 'month' ? 'Este mes' : 
                   period === 'quarter' ? 'Este trimestre' : 'Este año';
    }
    doc.text(periodText, 14, 30);
    
    // Add summary section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen de Cuenta', 14, 45);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Facturación Total: ${formatCurrency(totalBilling)}`, 20, 55);
    doc.text(`Pagos Recibidos: ${formatCurrency(totalPayments)}`, 20, 65);
    doc.text(`Saldo Actual: ${formatCurrency(currentBalance)}`, 20, 75);
    
    // Add transactions table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Movimientos de Cuenta', 14, 90);
    
    // Prepare table data
    const tableColumn = [
      'Fecha', 
      'Vencimiento',
      'Descripción', 
      'Tipo', 
      'Monto', 
      'Saldo'
    ];
    
    const tableRows = transactionsWithBalances.map(transaction => [
      formatArgentinaDate(transaction.date),
      transaction.type === 'invoice' && transaction.due_date ? formatArgentinaDate(transaction.due_date) : '-',
      transaction.description + (transaction.voided ? ' (Anulado)' : ''),
      transaction.voided ? 'Anulado' : 
        transaction.type === 'invoice' ? 'Factura' : 'Pago',
      (transaction.type === 'payment' ? '-' : '') + formatCurrency(transaction.amount),
      formatCurrency(transaction.runningBalance)
    ]);
    
    // Add the table to the PDF
    (doc as any).autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 95,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Fecha
        1: { cellWidth: 25 }, // Vencimiento
        2: { cellWidth: 'auto' }, // Descripción
        3: { cellWidth: 25 }, // Tipo
        4: { cellWidth: 30, halign: 'right' }, // Monto
        5: { cellWidth: 30, halign: 'right' }, // Saldo
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 95 }
    });
    
    // Add footer with date
    const pageCount = (doc as any).internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150);
    for(let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Generado el ${formatArgentinaDate(new Date().toISOString())}`, 14, doc.internal.pageSize.height - 10);
      doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 25, doc.internal.pageSize.height - 10);
    }
    
    // Save the PDF
    doc.save(`cuenta-cliente-${client.name.replace(/\s+/g, '-')}.pdf`);
  };
  
  // If client not found, show error message
  if (!client) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-12rem)]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Cliente no encontrado</h2>
          <p className="text-gray-600">El cliente que buscas no existe o no tienes acceso.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{client.name}</h2>
          <p className="text-sm text-gray-500">{client.email}</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="primary" 
            icon={<FileDown className="h-4 w-4" />}
            onClick={generatePDF}
          >
            Exportar PDF
          </Button>
          <Button 
            variant="outline" 
            icon={<Edit className="h-4 w-4" />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Editar Cliente
          </Button>
          <Button 
            variant="outline" 
            icon={<FileText className="h-4 w-4" />}
            onClick={() => setIsInvoiceModalOpen(true)}
          >
            Nueva Factura
          </Button>
          <Button 
            variant="outline" 
            icon={<Wallet className="h-4 w-4" />}
            onClick={() => setIsPaymentModalOpen(true)}
          >
            Registrar Pago
          </Button>
          <Button 
            variant="outline" 
            icon={<FileText className="h-4 w-4" />}
            onClick={() => setIsCreditNoteModalOpen(true)}
          >
            Nota de Crédito
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Facturación Total</p>
                <p className="mt-2 text-2xl font-semibold text-indigo-600">
                  {formatCurrency(totalBilling)}
                </p>
              </div>
              <div className="p-2 bg-indigo-50 rounded-lg">
                <ArrowUpRight className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pagos Recibidos</p>
                <p className="mt-2 text-2xl font-semibold text-emerald-600">
                  {formatCurrency(totalPayments)}
                </p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg">
                <ArrowDownRight className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Saldo Actual</p>
                <p className={`mt-2 text-2xl font-semibold ${
                  currentBalance > 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {formatCurrency(currentBalance)}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${
                currentBalance > 0 ? 'bg-emerald-50' : 'bg-red-50'
              }`}>
                {currentBalance > 0 ? (
                  <ArrowUpRight className="h-6 w-6 text-emerald-600" />
                ) : (
                  <ArrowDownRight className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader title="Movimientos de Cuenta" />
        <CardContent>
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              label="Período"
              value={period}
              onChange={(value) => {
                setPeriod(value);
                setStartDate('');
                setEndDate('');
              }}
              options={[
                { value: 'all', label: 'Todo' },
                { value: 'month', label: 'Este mes' },
                { value: 'quarter', label: 'Este trimestre' },
                { value: 'year', label: 'Este año' },
                { value: 'custom', label: 'Personalizado' },
              ]}
            />
            
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </>
            )}

            <Select
              label="Tipo de Factura"
              value={selectedInvoiceType}
              onChange={(value) => setSelectedInvoiceType(value as typeof selectedInvoiceType)}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'A', label: 'Factura A' },
                { value: 'B', label: 'Factura B' },
                { value: 'C', label: 'Factura C' },
                { value: 'X', label: 'Factura X' },
              ]}
            />

            <Select
              label="Tipo de Movimiento"
              value={selectedTransactionType}
              onChange={(value) => setSelectedTransactionType(value as typeof selectedTransactionType)}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'invoice', label: 'Facturas' },
                { value: 'payment', label: 'Pagos' },
              ]}
            />
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vencimiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Saldo
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactionsWithBalances.map((transaction) => (
                  <tr 
                    key={transaction.id} 
                    className={`hover:bg-gray-50 ${
                      transaction.voided ? 'bg-red-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatArgentinaDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.type === 'invoice' && transaction.due_date ? 
                        formatArgentinaDate(transaction.due_date) : 
                        '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.description}
                      {transaction.voided && (
                        <span className="ml-2 text-red-600 text-xs">(Anulado)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          transaction.voided
                            ? 'bg-red-100 text-red-800'
                            : transaction.type === 'invoice'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {transaction.voided
                          ? 'Anulado'
                          : transaction.type === 'invoice'
                          ? 'Factura'
                          : 'Pago'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <span
                        className={
                          transaction.type === 'payment' && transaction.invoiceType !== 'credit_note'
                            ? 'text-emerald-600 dark:text-emerald-400'
                          : transaction.type === 'payment' && transaction.invoiceType === 'credit_note'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-blue-600 dark:text-blue-400'
                        }
                      >
                        {transaction.type === 'payment' && transaction.invoiceType !== 'credit_note' && '-'}
                        {transaction.type === 'payment' && transaction.invoiceType === 'credit_note' && '-'}
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <span className={transaction.runningBalance > 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {formatCurrency(transaction.runningBalance)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <div className="flex justify-center gap-2">
                        {transaction.type === 'invoice' && transaction.invoice_file && (
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Eye className="h-4 w-4" />}
                            onClick={() => window.open(transaction.invoice_file, '_blank')}
                          >
                            Ver Factura
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Download className="h-4 w-4" />}
                          onClick={transaction.document}
                        >
                          PDF
                        </Button>
                        {!transaction.voided && (
                          <Button
                            variant="danger"
                            size="sm"
                            icon={<Trash2 className="h-4 w-4" />}
                            onClick={() => {
                              setItemToDelete({
                                id: transaction.id,
                                type: transaction.type
                              });
                              setDeleteModalOpen(true);
                            }}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <InvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        entity_id={client.id}
        entity_type="client"
        entityName={client.name}
      />
      
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        entityId={client.id}
        entityName={client.name}
        entityType="client"
      />
      
      <CreditNoteModal
        isOpen={isCreditNoteModalOpen}
        onClose={() => setIsCreditNoteModalOpen(false)}
        entityId={client.id}
        entityName={client.name}
        entityType="client"
      />

      <EntityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entityType="client"
        onSave={async (updatedClient) => {
          if (id) {
            await updateClient(id, updatedClient);
          }
          setIsEditModalOpen(false);
        }}
        initialData={client}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title={`Eliminar ${itemToDelete?.type === 'invoice' ? 'Factura' : 'Pago'}`}
        message={`¿Estás seguro de que deseas eliminar ${itemToDelete?.type === 'invoice' ? 'esta factura' : 'este pago'}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};

export default ClientAccount;